import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { updateWordCloudOptions } from '../../redux/ChartPoperties/ChartControlsActions';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  IconButton,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Types
type LocationType = 'start' | 'middle' | 'end' | 'everywhere';
type IgnoreEntry = { word: string; location: LocationType };

const selectStyle = {
  fontSize: '12px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2fcbcd' },
  textAlign: 'start',
  height: '1.5rem',
};

const menuItemStyle = {
  fontSize: '12px',
  padding: '4px 8px',
};

const WordCloudIgnoreWords = ({ iconColor }: { iconColor?: string }) => {
  const dispatch = useDispatch();
  const { tabTileProps, chartControls } = useSelector((state: RootState) => ({
    tabTileProps: state.tabTileProps,
    chartControls: state.chartControls,
  }));

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  // load ignore words from store
  const rawIgnore: any[] = chartControls.properties[propKey]?.wordCloudOptions?.ignoreWords || [];

  const ignoreWords: IgnoreEntry[] = (rawIgnore || []).map((w) =>
    typeof w === 'string'
      ? { word: String(w), location: 'everywhere' }
      : { word: String(w.word || w), location: w.location || 'everywhere' },
  );

  const [input, setInput] = useState('');
  const [defaultLocation, setDefaultLocation] = useState<LocationType>('everywhere');

  const normalized = useMemo(() => {
    const map = new Map<string, IgnoreEntry>();
    (ignoreWords || []).forEach((e) => {
      const word = String(e.word || '').trim();
      if (!word) return;
      const lower = word.toLowerCase();
      if (!map.has(lower)) map.set(lower, { word, location: e.location || 'everywhere' });
    });
    return Array.from(map.values());
  }, [ignoreWords]);

  const addWords = () => {
    //  ONLY trimming whitespace.not removing punctuation.
    const wordToAdd = input.trim();
    if (!wordToAdd) return;

    const mergedMap = new Map<string, IgnoreEntry>();
    normalized.forEach((e) => mergedMap.set(e.word.toLowerCase(), e));

    const lower = wordToAdd.toLowerCase();

    //  Add to map if not present
    if (!mergedMap.has(lower)) {
      mergedMap.set(lower, { word: wordToAdd, location: defaultLocation });

      const merged = Array.from(mergedMap.values());
      dispatch(updateWordCloudOptions(propKey, 'ignoreWords', merged));
    }

    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWords();
    }
  };

  const removeWord = (w: string) => {
    const updated = normalized.filter((x) => x.word.toLowerCase() !== w.toLowerCase());
    dispatch(updateWordCloudOptions(propKey, 'ignoreWords', updated));
  };

  return (
    <Box sx={{ width: '100%', p: 1, boxSizing: 'border-box' }}>
      <div
        className="optionDescription"
        style={{ marginTop: '5px', paddingLeft: '.1rem', color: iconColor ? iconColor : '#404040' }}
      >
        Word Position
      </div>
      <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
        <Select
          value={defaultLocation}
          variant="outlined"
          onChange={(e: SelectChangeEvent) => setDefaultLocation(e.target.value as LocationType)}
          sx={{
            ...selectStyle,
            color: iconColor || '#404040',
            '& .MuiSvgIcon-root': { color: iconColor || '#404040' },
          }}
          MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': menuItemStyle, ml: 0.5 } } }}
        >
          <MenuItem value="everywhere">Everywhere</MenuItem>
          <MenuItem value="start">Start</MenuItem>
          <MenuItem value="middle">Middle</MenuItem>
          <MenuItem value="end">End</MenuItem>
        </Select>
      </FormControl>
      {/* // Input for Adding Words */}
      <div
        className="optionDescription"
        style={{ marginTop: '8px', paddingLeft: '.1rem', color: iconColor ? iconColor : '#404040' }}
      >
        Words to Ignore
      </div>
      <TextField
        fullWidth
        size="small"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type word... and Press Enter"
        variant="outlined"
        sx={{
          '& .MuiInputBase-root': {
            fontSize: '12px',
            height: '2rem',
            color: iconColor || '#404040',
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: iconColor || '#ccc' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: iconColor || '#999' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: iconColor || '#2fcbcd' },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={addWords}
                edge="end"
                size="small"
                disabled={!input.trim()}
                color="primary"
              >
                <AddCircleOutlineIcon fontSize="small" sx={{ color: iconColor || '#404040' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {/* // Ignored Words List */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {normalized.length > 0 ? (
          normalized.map((e) => (
            <Chip
              key={e.word}
              label={e.word}
              onDelete={() => removeWord(e.word)}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: '#f0f0f0',
                fontSize: '11px',
                height: '24px',
              }}
            />
          ))
        ) : (
          <div
            style={{
              color: iconColor ? iconColor : '#999',
              fontSize: '12px',
              fontStyle: 'italic',
              marginTop: '2px',
            }}
          >
            No ignored words added.
          </div>
        )}
      </Box>
    </Box>
  );
};

export default WordCloudIgnoreWords;
